import React, { useCallback, useEffect, useState } from 'react';
import { Field, Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ComboBox,
  Button,
  Radio,
  Toggler,
  TextInput,
  LoadingIndicator,
  Icons,
} from '@components';
import { MATInitialValues, MATSchema, MATDocs } from '@schemas';
import { enviaFormRequest } from '@store/user/actions';
import { constantesRequerimentos } from '@utils';
import { mdiFileDownloadOutline } from '@mdi/js';
import { LimitePorcentagemSaqueService } from '@services';
import { ModalUpload } from './components/ModalUpload';
import { RequerimentoService } from '../../../../common/services/requerimento';
import { DadosUsuario } from './components/DadosUsuario';

const {
  VITALICIA_S_R_RCM,
  VITALICIA_C_R_RCM,
  CONJUGADA_S_R_RCM,
  CONJUGADA_C_R_RCM,
  TEMPORARIA_FIXO,
  TEMPORARIA_VARIAVEL,
} = constantesRequerimentos;

const Anexos = ({
  estaAposentado,
  foiJulgado,
  houveProcessoJudicial,
  resetAnexos,
}) => {
  const [doc, setDoc] = useState([]);
  const docs = MATDocs;
  const [modalIsOpen, setModalIsOpen] = useState(false);

  useEffect(() => {
    if (estaAposentado === 'Sim') {
      setDoc(MATDocs.Aposentado);
    }

    if (
      estaAposentado === 'Sim' &&
      houveProcessoJudicial === 'Sim' &&
      foiJulgado === 'Não'
    ) {
      setDoc(MATDocs.Tutela);
    }

    if (estaAposentado === 'Não') {
      setDoc(MATDocs.NaoAposentado);
    }
  }, [estaAposentado, foiJulgado, houveProcessoJudicial]);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {docs.commons.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
              key={resetAnexos}
            >
              {({ form }) => {
                return (
                  <TextInput
                    key={resetAnexos}
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return form.setFieldValue(
                        anexo.name,
                        e.target.value ? e.currentTarget.files : null
                      );
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}

      {doc.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              key={resetAnexos}
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    key={resetAnexos}
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return form.setFieldValue(
                        anexo.name,
                        e.target.value ? e.currentTarget.files : null
                      );
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

const service = new RequerimentoService();

export const MAT = ({ title, reference }) => {
  const [fileURL, setFileURL] = useState('');
  const [loading, setLoading] = useState(true);
  const [limiteSaque, setLimiteSaque] = useState(50);
  const user = useSelector((state) => state.user);

  useEffect(() => {
    service
      .obterTermoJudicial()
      .then((URL) => setFileURL(URL))
      .finally(() => setLoading(false));
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { requestDone, saldoDevedor } = useSelector(
    (state) => state.emprestimo.limite
  );

  const [showForm, setShowForm] = useState(false);

  const recuperaLimitePercSaque = useCallback(async () => {
    const limitePorcentagemSaqueService = new LimitePorcentagemSaqueService();

    const response =
      await limitePorcentagemSaqueService.getLimitePorcentagemSaque(
        user.matricula
      );
    setLimiteSaque(response.data.percentualSaqueVista?.slice(-1));
  }, []);

  useEffect(() => {
    recuperaLimitePercSaque();
  }, [recuperaLimitePercSaque]);

  return !requestDone ? (
    <LoadingIndicator />
  ) : (
    <div className="card">
      <div className="card-body">
        {showForm ? (
          <Formik
            validateOnMount
            initialValues={MATInitialValues}
            validationSchema={MATSchema(saldoDevedor, limiteSaque)}
            onSubmit={(values) => {
              dispatch(
                enviaFormRequest({
                  campos: values,
                  formulario: reference,
                  navigate,
                })
              );
            }}
          >
            {({ values, handleSubmit, isValid, resetForm, setFieldValue }) => (
              <Form>
                <h3 className="fw-bold ts-20 mb-32">
                  Opção de Regime de Tributação
                </h3>

                <p className="mb-32">
                  Escolha o Regime de Tributação do Imposto de Renda que será
                  calculado no seu requerimento com base na Lei nº 11.053 de
                  2004, alterada pela Lei nº 14.803 de 2024. Essa Lei permite
                  aos participantes de planos de previdência a escolha de seu
                  regime de tributação na ocasião da obtenção do benefício ou do
                  primeiro resgate dos valores acumulados.
                </p>

                <p className="mb-32">
                  <strong>Formalizo a minha opção pelo Regime: </strong>
                </p>

                <div className="row">
                  <Field
                    className="mb-5 ms-3"
                    name="tipoRegime"
                    component={Radio}
                    value="Progressivo"
                    label="Progressivo: os valores recebidos da Forluz serão tributados conforme tabela para cálculo do Imposto de Renda (IR), o que significa que o desconto do imposto na fonte terá alíquotas variáveis conforme o montante recebido, ou seja, quanto maior o valor, maior a alíquota. Atualmente, as alíquotas variam de 0% a 27,5%. Nesse regime poderão ocorrer deduções mensais e ajustes na declaração anual de Imposto de Renda. "
                  />
                </div>

                <div className="row">
                  <Field
                    className="mb-3 ms-3"
                    name="tipoRegime"
                    component={Radio}
                    value="Regressivo"
                    label="Regressivo: neste caso, o IR pago no recebimento da Forluz vai variar entre 35% e 10% de acordo com o tempo de aporte de recursos ao plano, ou seja, quanto maior o tempo, menor a alíquota. A tributação será exclusivamente na fonte, ou seja, não serão realizados ajustes anuais na declaração de Imposto de Renda. "
                  />
                </div>

                <p className="mb-32 mt-32">
                  <strong>Declarações</strong>
                </p>

                <div className="row">
                  <Field
                    className="mb-3"
                    name="aceite"
                    component={Toggler}
                    label="Declaro ciência de que a opção marcada é IRRETRATÁVEL, ou seja, saques posteriores ou resgates terão a tributação calculada de acordo com essa escolha."
                  />
                </div>

                <div className="row">
                  <Field
                    className="mb-3"
                    name="declaracaoTipoRegime"
                    component={Toggler}
                    label="Declaro ciência de que caso já tenha feito algum resgate, será desconsiderada a opção marcada acima e será aplicado o regime escolhido no primeiro resgate. "
                  />
                </div>

                <hr className="mt-32 mt-xs-0 mb-32" />
                <h3 className="fw-bold ts-20 mb-32">{title}</h3>

                <p className="mb-4">
                  Venho, por meio deste, requerer à Fundação Forluminas de
                  Seguridade Social - FORLUZ o benefício de Melhoria de
                  Aposentadoria por Tempo de Contribuição, Especial ou por
                  Idade, cujo pagamento será realizado conforme minhas opções
                  neste requerimento, de acordo com o Regulamento do Plano Misto
                  de Benefícios Previdenciários da Forluz (Plano B), as normas
                  vigentes e aquelas que venham a alterá-las ou substituí-las.
                  Para tanto, anexo a documentação necessária para a finalidade
                </p>

                {loading ? (
                  <p className="mb-4">Carregando termo de compromisso...</p>
                ) : (
                  <a
                    href={fileURL}
                    rel="noreferrer"
                    target="_blank"
                    download
                    title="Clique para termo de compromisso"
                    className="w-250 mb-4 d-md-flex ts-14 mb-2 mt-4 align-items-center justify-content-between text-decoration-none fw-bold primary-color"
                  >
                    Baixar termo de compromisso
                    <Icons
                      path={mdiFileDownloadOutline}
                      color="var(--primary-color)"
                    />
                  </a>
                )}
                <div className="row">
                  <div className="col-md-12 col-lg-6 mb-3">
                    <p className="label mb-3">Opção benefício</p>
                    <Field
                      className="mb-3"
                      name="opcaoBeneficio"
                      component={Radio}
                      tooltip="Benefício pago ao participante por toda a vida e não será convertido em RCM, pois este benefício que não deixa pensão."
                      value={VITALICIA_S_R_RCM}
                      label={VITALICIA_S_R_RCM}
                      onChange={(e) => {
                        resetForm({
                          values: {
                            ...MATInitialValues,
                            tipoRegime: values.tipoRegime,
                            aceite: values.aceite,
                            declaracaoTipoRegime: values.declaracaoTipoRegime,
                          },
                        });
                        setFieldValue('opcaoBeneficio', e.target.value, false);
                      }}
                    />
                    <Field
                      className="mb-3"
                      name="opcaoBeneficio"
                      component={Radio}
                      value={VITALICIA_C_R_RCM}
                      label={VITALICIA_C_R_RCM}
                      tooltip="Benefício pago ao participante por toda a vida e ao falecer a MAT será convertida em RCM (pensão) a ser paga em partes iguais aos beneficiários inscritos."
                      onChange={(e) => {
                        resetForm();
                        setFieldValue('opcaoBeneficio', e.target.value, false);
                      }}
                    />
                    <Field
                      className="mb-3"
                      name="opcaoBeneficio"
                      component={Radio}
                      value={TEMPORARIA_FIXO}
                      label={TEMPORARIA_FIXO}
                      tooltip="Benefício pago ao participante pelo prazo determinado pelo participante no requerimento. O saldo de conta será esgotado no encerramento do benefício."
                      onChange={(e) => {
                        resetForm();
                        setFieldValue('opcaoBeneficio', e.target.value, false);
                      }}
                    />
                    <Field
                      className="mb-3"
                      name="opcaoBeneficio"
                      component={Radio}
                      value={TEMPORARIA_VARIAVEL}
                      label={TEMPORARIA_VARIAVEL}
                      tooltip="Benefício financeiro a ser pago ao participante enquanto durar o saldo."
                      onChange={(e) => {
                        resetForm();
                        setFieldValue('opcaoBeneficio', e.target.value, false);
                      }}
                    />
                    <Field
                      className="mb-3"
                      name="opcaoBeneficio"
                      component={Radio}
                      value={CONJUGADA_S_R_RCM}
                      label={CONJUGADA_S_R_RCM}
                      tooltip="Mínimo de 50% do saldo destinado ao benefício Vitalício sem reversão em RCM (pensão por morte). O restante do saldo destinado à renda temporária em valor variável, calculada com base no saldo remanescente. "
                      onChange={(e) => {
                        resetForm();
                        setFieldValue('opcaoBeneficio', e.target.value, false);
                      }}
                    />
                    <Field
                      className="mb-3"
                      name="opcaoBeneficio"
                      component={Radio}
                      value={CONJUGADA_C_R_RCM}
                      label={CONJUGADA_C_R_RCM}
                      tooltip="Mínimo de 50% do saldo destinado ao benefício Vitalício sem reversão em RCM (pensão por morte). O restante do saldo destinado à renda temporária em valor variável, calculada com base no saldo remanescente. "
                      onChange={(e) => {
                        resetForm();
                        setFieldValue('opcaoBeneficio', e.target.value, false);
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <Field
                      className="mb-3"
                      name="parcelaAVista"
                      component={ComboBox}
                      tooltip={`Retirada antecipada de até ${limiteSaque}% do saldo de conta; acarreta redução proporcional no valor do benefício. Haverá incidência de Imposto de Renda conforme regime de tributação escolhido e o saldo devedor do empréstimo será descontado (antecipação do prazo) na mesma proporção da sua retirada.`}
                      options={['Sim', 'Não'].map((x) => {
                        return { value: x, label: x };
                      })}
                      onChange={() => {
                        setFieldValue('retiradaAVista', '', false);
                      }}
                      label="Parcela à Vista"
                    />
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <Field
                      disabled={!(values.parcelaAVista === 'Sim')}
                      className="mb-3"
                      name="retiradaAVista"
                      label="Percentual retirada à vista"
                      component={TextInput}
                      type="number"
                      min="0"
                      max={limiteSaque}
                    />
                  </div>
                </div>
                {!!saldoDevedor && values.parcelaAVista === 'Sim' && (
                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="boletoEmprestimo"
                        component={ComboBox}
                        tooltip="Boleto para quitação integral do saldo devedor remanescente será encaminhado por e-mail do endereço de e-mail cadastrado com vencimento no último dia útil do mês seguinte ao requerimento de benefício."
                        options={['Sim', 'Não'].map((x) => {
                          return { value: x, label: x };
                        })}
                        label="Boleto para quitação de empréstimo"
                        onChange={() => {
                          setFieldValue('abatimentoDoEmprestimo', '', false);
                        }}
                      />
                    </div>
                    {values.boletoEmprestimo === 'Não' && (
                      <div className="col-md-12 col-lg-6">
                        <Field
                          className="mb-3"
                          name="abatimentoDoEmprestimo"
                          component={ComboBox}
                          tooltip="Antecipação:	Modalidade de pagamento em que prazo final do contrato é reduzido ( abatimento das últimas parcelas) e o valor da parcela mantido. Nesta modalidade o montante de juros é reduzido em função do encurtamento do contrato. Amortização:	Modalidade de pagamento em que o valor da parcela é reduzido e o prazo contratado mantido. "
                          options={['Antecipação', 'Amortização'].map((x) => {
                            return { value: x, label: x };
                          })}
                          label="Abatimento do empréstimo"
                        />
                      </div>
                    )}
                  </div>
                )}

                {[CONJUGADA_S_R_RCM, CONJUGADA_C_R_RCM].includes(
                  values.opcaoBeneficio
                ) && (
                  <div className="row">
                    <div className="col-md-12 col-lg-6 mb-3">
                      <Field
                        label="Percentual do saldo de contas em Renda Vitalícia"
                        name="percentualSaldoDeContasEmRendaVitalicia"
                        component={TextInput}
                        min={50}
                        max={100}
                        type="number"
                      />
                    </div>
                  </div>
                )}

                {[
                  TEMPORARIA_VARIAVEL,
                  CONJUGADA_S_R_RCM,
                  CONJUGADA_C_R_RCM,
                ].includes(values.opcaoBeneficio) && (
                  <div className="row">
                    <div className="col-md-12 col-lg-6 mb-3">
                      <Field
                        label="Percentual retirada mensal"
                        name="percentualRetiradaMensal"
                        component={TextInput}
                        min={1}
                        max={100}
                        type="number"
                      />
                    </div>
                  </div>
                )}

                {[TEMPORARIA_FIXO].includes(values.opcaoBeneficio) && (
                  <div className="row">
                    <div className="col-md-12 col-lg-6 mb-3">
                      <Field
                        label="Prazo"
                        name="prazo"
                        component={ComboBox}
                        options={['10 anos', '15 anos', '20 anos'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div>
                  </div>
                )}

                {[VITALICIA_C_R_RCM, CONJUGADA_C_R_RCM].includes(
                  values.opcaoBeneficio
                ) && (
                  <div className="row">
                    <div className="col-md-12 col-lg-6 mb-3">
                      <Field
                        label="Percentual RCM"
                        name="percentualRCM"
                        component={TextInput}
                        min={1}
                        max={100}
                        type="number"
                      />
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <Field
                      className="mb-3"
                      name="estaAposentadoINSS"
                      component={ComboBox}
                      options={['Sim', 'Não'].map((x) => {
                        return { value: x, label: x };
                      })}
                      label="Está aposentado pelo INSS?"
                      onChange={(op) => {
                        if (op.value === 'Não') {
                          setFieldValue('houveProcessoJudicial', '', false);
                          setFieldValue('foiJulgado', '', false);
                          setFieldValue('atividadeRural', '', false);
                          setFieldValue('processoJudicial', '', false);
                        }
                      }}
                    />
                  </div>

                  <div className="col-md-12 col-lg-6">
                    <Field
                      disabled={!(values.estaAposentadoINSS === 'Sim')}
                      className="mb-3"
                      name="houveProcessoJudicial"
                      component={ComboBox}
                      options={['Sim', 'Não'].map((x) => {
                        return { value: x, label: x };
                      })}
                      label="Houve processo judicial?"
                      onChange={(op) => {
                        if (op.value === 'Não') {
                          setFieldValue('foiJulgado', '', false);
                          setFieldValue('processoJudicial', '', false);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  {values.houveProcessoJudicial === 'Sim' && (
                    <div className="col-md-12 col-lg-6">
                      <Field
                        disabled={!(values.houveProcessoJudicial === 'Sim')}
                        className="mb-3"
                        name="processoJudicial"
                        component={TextInput}
                        type="number"
                        label="Número do processo judicial"
                      />
                    </div>
                  )}

                  <div className="col-md-12 col-lg-6">
                    <Field
                      disabled={!(values.houveProcessoJudicial === 'Sim')}
                      className="mb-3"
                      name="foiJulgado"
                      component={ComboBox}
                      options={['Sim', 'Não'].map((x) => {
                        return { value: x, label: x };
                      })}
                      label="Transitou em julgado?"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <Field
                      className="mb-3"
                      name="acessoContraCheque"
                      label="Acesso ao contracheque via"
                      component={ComboBox}
                      options={['Portal', 'E-mail e portal'].map((x) => {
                        return { value: x, label: x };
                      })}
                    />
                  </div>

                  {/* <div className="col-md-12 col-lg-6">
                    <Field
                      className="mb-3"
                      name="acessoJornalForluz"
                      component={ComboBox}
                      label="Acesso Jornal Forluz"
                      options={['Digital', 'Impresso'].map((x) => {
                        return { value: x, label: x };
                      })}
                    />
                  </div> */}
                </div>

                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <Field
                      className="mb-3"
                      name="adiantamentoQuinzenal"
                      component={ComboBox}
                      tooltip="Antecipação de 35% do valor bruto, pago no dia 15 de cada mês."
                      label="Adiantamento quinzenal?"
                      options={['Sim', 'Não'].map((x) => {
                        return { value: x, label: x };
                      })}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 ">
                    <Field
                      className="mb-3"
                      name="consentimentoDescontoFolha"
                      component={Toggler}
                      label="Declaro estar ciente de que a Forluz operacionalizará os
                      descontos decorrentes de eventual relacionamento com as
                      Entidades Externas (ABCF; AEA; AIC; ANAPAR; CECREMEC;
                      CEMIG SAÚDE; CLIC; GREMIG; SEGURO DE VIDA; SINDICATO JUIZ
                      DE FORA; SINDIELETRO; SINDSUL), por meio de descontos no
                      meu benefício para repasse à(s) Entidade(s), os quais
                      poderão ser visualizados mensalmente no contracheque.
                      Declaro estar ciente, ainda, de que devo contatar
                      diretamente a Entidade à qual sou filiado para o
                      cancelamento das cobranças e/ou ressarcimento de qualquer
                      valor."
                    />

                    <Field
                      className="mb-3"
                      name="consentimentoAjustePagamento"
                      component={Toggler}
                      createTermsField
                      label="Autorizo que, em caso de falecimento, créditos e débitos oriundos de resíduos e adiantamentos anteriores ao óbito, ou valores processados indevidamente após essa data, sejam descontados ou creditados em favor dos meus beneficiários de renda continuada por morte do plano de benefícios."
                    />

                    <Field
                      className="mb-3"
                      name="consentimentoDadosSensiveis"
                      component={Toggler}
                      label="Com base na Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais), autorizo o tratamento dos meus dados pessoais e dados pessoais sensíveis para fins relacionados ao meu vínculo com a FORLUZ."
                    />
                  </div>
                </div>

                {[
                  TEMPORARIA_VARIAVEL,
                  CONJUGADA_S_R_RCM,
                  CONJUGADA_C_R_RCM,
                ].includes(values.opcaoBeneficio) && (
                  <div className="row">
                    <p className="mb-4">
                      Ao optar pela {values.opcaoBeneficio}, declaro ter pleno
                      conhecimento de que: 1. A rentabilidade mensal aplicável
                      sobre o saldo da minha conta após entrar em gozo de
                      benefício será aquela auferida sobre os recursos
                      garantidores do perfil da minha escolha. Caso não opte
                      pelo perfil, a Diretoria definirá o pefil no qual serão
                      investidos os respectivos recursos. O saldo da minha conta
                      dependerá da rentabilidade obtida e do valor do benefício.
                      2. Não há garantia de rentabilidade mínima, podendo,
                      inclusive, ocorrer rentabilidade negativa. 3. O valor do
                      benefício dependerá do saldo da conta e do percentual
                      escolhido e será recalculado anualmente. 4. O benefício se
                      encerrará quando se exaurir o saldo existente em minha
                      conta. 5. Caso opte posteriormente por converter o
                      benefício em Renda Vitalícia ou em Renda Conjugada, essa
                      será calculada considerando o saldo de conta remanescente
                      e o Fator de Conversão Atuarial na data da nova opção.
                    </p>
                    <Field
                      className="mb-3"
                      name="consentimentoBeneficio"
                      component={Toggler}
                      label="Consentimento opção de benefício"
                    />
                  </div>
                )}

                <Field
                  className="mb-3"
                  name="aceiteInss"
                  component={Toggler}
                  createTermsField
                  label="Estou ciente que pessoa aposentada pela Previdência Oficial (INSS) e acometida por moléstia grave faz jus à isenção de Imposto de Renda. Este pedido poderá ser solicitado à Forluz a qualquer tempo através do formulário Isenção de Imposto de Renda disponível no menu Requerimentos. Se deferida, a isenção será processada no mês seguinte ao requerimento. "
                />

                <hr className="mt-32 mt-xs-0 mb-32" />

                <Anexos
                  estaAposentado={values.estaAposentadoINSS}
                  foiJulgado={values.foiJulgado}
                  houveProcessoJudicial={values.houveProcessoJudicial}
                  resetAnexos={values.opcaoBeneficio}
                />

                <Field
                  className="mb-3"
                  name="declaracaoVeracidade"
                  component={Toggler}
                  label="Declaro que as informações e os documentos por mim apresentados à Forluz são verdadeiros, estando ciente de que, caso não sejam autênticos, estarei sujeito às consequências civis e criminais cabíveis, além de autorizar a Forluz a anular o ato praticado ou o benefício concedido."
                />

                <hr className="mt-32 mt-xs-0 mb-32" />

                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        disabled={!isValid}
                        className="ms-0 ms-md-3"
                        onClick={handleSubmit}
                      >
                        Continuar
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <DadosUsuario
            onClick={() => {
              window.scroll(0, 0);
              setShowForm(true);
            }}
            dadosBancarios
          />
        )}
      </div>
    </div>
  );
};
